import * as R from 'fp-ts/Record';

import { IncomingHttpHeaders } from 'http';
import { GetServerSidePropsResult, GetStaticPropsResult, Redirect } from 'next';

import { CommonData } from '@domains/common-data';
import { PageKind } from '@domains/global-data';
import { User } from '@domains/user';
import { WithGlobalData } from '@domains/view';

import { ApiError } from '@api/client/errors';
import { ProductsCatalogCategoriesListResult } from '@api/protocol';

import { isValueSet } from '@utils/guards';
import { proxyHeaders } from '@utils/network';
import { Routes } from '@utils/route';
import { trackException } from '@utils/sentry';

import { Authorization, CookieAuth } from './auth';
import { ApiClientClass } from './index';
import { RequestPayload } from './types';

type BFFConfig = {
    isCommonDataFetchEnabled?: boolean;
    isCategoriesListFetchEnabled?: boolean;
    revalidateTimeInMinutes?: number;
    isPrivateRoute?: boolean;
    isNotFoundOnErrorShown?: boolean;
    request?: BFF.Req;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StackItem<T, P extends RequestPayload, D = any> = {
    prop: T;
    url?: string;
    data?: D;
    payload?: P;
};

export class BFFNotFoundError extends Error {}

class BFFRedirectError extends Error {
    public readonly redirect: Redirect;

    constructor(redirect: Redirect) {
        super(`Redirect to ${redirect.destination}`);
        this.redirect = redirect;
    }
}

class BFFClientClass<T> {
    private readonly config: BFFConfig = {
        isCommonDataFetchEnabled: true,
        isCategoriesListFetchEnabled: true,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private stack: StackItem<keyof T, any>[] = [];

    constructor(private readonly auth: Authorization, config?: BFFConfig) {
        if (config !== undefined) this.config = { ...this.config, ...config };
    }

    private initApiClient(headers?: IncomingHttpHeaders) {
        const backendEndpoint = process.env.BACKEND_ENDPOINT;
        const unthrottledToken = process.env.UNTHROTTLED_TOKEN;

        const ApiClient = new ApiClientClass(this.auth);
        ApiClient.setSentryHandler(trackException);
        if (backendEndpoint) ApiClient.setBackendEndpoint(backendEndpoint);
        if (unthrottledToken) ApiClient.setUnthrottledToken(unthrottledToken);
        if (headers !== undefined && !R.isEmpty(headers)) {
            const proxied = proxyHeaders(headers);
            // Sanctum requires Origin or Referer to identify stateful requests.
            // SSR (server-to-server) requests may not have Origin — add it explicitly
            // using the frontend URL (must be in SANCTUM_STATEFUL_DOMAINS).
            if (!proxied.origin && !proxied.referer) {
                const appUrl = process.env.APP_URL;
                const port = process.env.PORT || '3000';
                proxied.origin = appUrl || `http://localhost:${port}`;
            }
            ApiClient.setCustomHeaders(proxied);
        }
        return ApiClient;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public addToStack<P extends RequestPayload, D = any>(
        item: StackItem<keyof T, P, D>,
        position: 'start' | 'end' = 'end',
    ) {
        if (position === 'end') this.stack.push(item);
        if (position === 'start') this.stack.unshift(item);
        return this;
    }

    private fetchUrl<R, P extends RequestPayload = null>(url: string, payload?: P): Promise<R> {
        return this.initApiClient(this.config.request?.headers).get<R, P>(url, payload);
    }

    private addCommonDataToStack() {
        this.addToStack(
            {
                prop: 'globalData.commonData' as keyof T,
                url: '/api/common_data',
            },
            'start',
        );
    }

    private addCategoriesListToStack() {
        this.addToStack(
            {
                prop: 'globalData.categoriesList' as keyof T,
                url: '/api/products_catalog/categories/list',
            },
            'start',
        );
    }

    private async getData(pageKind: PageKind) {
        let response: T[keyof T][] | undefined;
        let commonData: CommonData | null = null;
        let categoriesList: ProductsCatalogCategoriesListResult | null = null;
        let promisesOffset = 0;
        try {
            if (this.config.isCommonDataFetchEnabled) this.addCommonDataToStack();
            if (this.config.isCategoriesListFetchEnabled) this.addCategoriesListToStack();

            response = await Promise.all(
                this.stack.map(({ prop, url, data, payload }) =>
                    url !== undefined
                        ? this.fetchUrl<T[typeof prop]>(url, payload)
                        : new Promise<T[typeof prop]>((resolve) => resolve(data)),
                ),
            );
            // TODO order matters, but should not
            if (this.config.isCategoriesListFetchEnabled) {
                categoriesList = response.shift() as unknown as ProductsCatalogCategoriesListResult;
            }
            if (this.config.isCommonDataFetchEnabled) {
                commonData = response.shift() as unknown as CommonData;
            }
        } catch (e: unknown) {
            const error = e as Error;
            const isApiNotFound = error instanceof ApiError && error.status === 404;

            if (this.config.isNotFoundOnErrorShown && isApiNotFound) {
                throw new BFFNotFoundError(error.message);
            }

            if (error instanceof ApiError && error.getCommonCodes().includes('invalid_token')) {
                throw new BFFRedirectError({
                    destination: Routes.Home.url,
                    permanent: false,
                });
            } else {
                trackException(error);
                throw new Error(error.message);
            }
        }

        if (this.config.isPrivateRoute && (commonData === null || commonData.user === null)) {
            throw new BFFRedirectError({
                destination: Routes.Home.url,
                permanent: false,
            });
        }

        if (this.config.isCommonDataFetchEnabled) promisesOffset++;
        if (this.config.isCategoriesListFetchEnabled) promisesOffset++;
        const result = response.reduce((acc, value, index) => {
            return {
                ...acc,
                [this.stack[index + promisesOffset].prop]: value,
            };
        }, {}) as T;

        return {
            ...result,
            globalData: {
                commonData,
                categoriesList,
                user: commonData && commonData.user ? commonData.user : null,
                pageKind,
            },
        } as WithGlobalData<T>;
    }

    public async getStaticProps(): Promise<GetStaticPropsResult<WithGlobalData<T>>> {
        const { revalidateTimeInMinutes } = this.config;

        if (!isValueSet(revalidateTimeInMinutes)) {
            throw new Error('revalidate prop is not set, use BFF config');
        }

        return {
            props: await this.getData('static'),
            revalidate: revalidateTimeInMinutes,
        };
    }

    public async getServerProps(): Promise<GetServerSidePropsResult<WithGlobalData<T>>> {
        try {
            const props = await this.getData('server');
            const hasSherlockHeader = this.config.request?.headers['x-sherlock'] === '1';

            return {
                props: {
                    ...props,
                    globalData: {
                        ...props.globalData,
                        hasSherlockHeader,
                    },
                },
            };
        } catch (error: unknown) {
            if (error instanceof BFFNotFoundError) {
                return {
                    notFound: true,
                };
            }

            if (error instanceof BFFRedirectError) {
                return {
                    redirect: error.redirect,
                };
            }

            throw new Error((error as Error).message);
        }
    }

    public async fetchUser(): Promise<User> {
        const data = await this.fetchUrl<{ user: User }>('/api/user/profile/details');
        return data.user;
    }
}

export const BFFCookieClientClass = <T>(config?: BFFConfig) =>
    new BFFClientClass<T>(CookieAuth(), config);
